/* ============================================================================
   BITÁCORA DE OPERACIONES — acumulados, cotejo y verificación de arrastre.

   La bitácora virtual es la fuente que alimenta dos cosas distintas:

     · El STATUS DE MANTENIMIENTO por aeronave: horas de célula y motor,
       ciclos NG y NF, aterrizajes y cargas. De ahí salen los remanentes de
       cada inspección y de cada componente de vida límite.

     · El DUTY TIME por piloto: horas voladas y DÍAS volados por mes y por año.

   La bitácora en papel no se edita ni se transcribe acá: es un documento
   manuscrito y firmado. Lo que se hace es COTEJARLA — el técnico compara lo
   cargado virtualmente contra lo escrito a mano y deja constancia de si
   coincide. Editar el escaneo para "arreglarlo" destruiría el único registro
   que vale como prueba.
   ========================================================================== */

/** Estados posibles del cotejo contra el documento físico. */
export const ESTADOS_COTEJO = {
  PENDIENTE: "pendiente",
  COINCIDE: "coincide",
  DISCREPA: "discrepa",
};

/** Campos que se cotejan uno a uno contra la bitácora de papel. */
export const CAMPOS_COTEJABLES = [
  /* Es AIR TIME: el programa de mantenimiento cuenta tiempo en el aire. */
  { clave: "horasAeronave", etiqueta: "Air time (célula)" },
  { clave: "horasMotor", etiqueta: "Horas de motor" },
  { clave: "ciclosNG", etiqueta: "Ciclos NG" },
  { clave: "ciclosNF", etiqueta: "Ciclos NF" },
  { clave: "ldg", etiqueta: "Aterrizajes" },
  { clave: "cargas", etiqueta: "Cargas externas" },
];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const redondear = (v, d = 1) => Math.round(v * 10 ** d) / 10 ** d;

/**
 * Cotejo de un asiento contra la bitácora de papel.
 *
 * El técnico marca campo por campo. Si algún campo no coincide, el asiento
 * entero queda como discrepante y deja de alimentar el status: un dato que se
 * sabe distinto del papel no puede seguir sumando horas de inspección.
 */
export function evaluarCotejo(cotejo = {}) {
  const marcas = cotejo.campos || {};
  const revisados = CAMPOS_COTEJABLES.filter((c) => marcas[c.clave] !== undefined);
  const distintos = revisados.filter((c) => marcas[c.clave] === false);

  if (revisados.length === 0) {
    return { estado: ESTADOS_COTEJO.PENDIENTE, distintos: [], completo: false, mensaje: "Sin cotejar contra la bitácora física." };
  }
  if (distintos.length > 0) {
    return {
      estado: ESTADOS_COTEJO.DISCREPA,
      distintos: distintos.map((c) => c.etiqueta),
      completo: revisados.length === CAMPOS_COTEJABLES.length,
      mensaje: `No coincide con el papel en: ${distintos.map((c) => c.etiqueta).join(", ")}. Este asiento no alimenta el status de mantenimiento.`,
    };
  }
  if (revisados.length < CAMPOS_COTEJABLES.length) {
    return {
      estado: ESTADOS_COTEJO.PENDIENTE,
      distintos: [],
      completo: false,
      mensaje: `Cotejo incompleto: ${revisados.length} de ${CAMPOS_COTEJABLES.length} campos revisados.`,
    };
  }
  return { estado: ESTADOS_COTEJO.COINCIDE, distintos: [], completo: true, mensaje: "Coincide con la bitácora física en todos los campos." };
}

/**
 * Suma de lo que la bitácora aporta a una aeronave.
 *
 * Solo se cuentan los vuelos que NO están marcados como discrepantes: un
 * asiento que el técnico marcó como distinto del papel no puede seguir
 * alimentando el status de mantenimiento como si fuera correcto.
 */
export function acumuladoDeAeronave(bitacora, aircraftId, { incluirDiscrepantes = false } = {}) {
  // `= []` en el parámetro no cubre un null explícito, y null es justo lo que
  // llega cuando la persistencia todavía no cargó.
  const lista = Array.isArray(bitacora) ? bitacora : [];
  const propios = lista.filter((v) => v && v.aircraftId === aircraftId);

  // El estado del cotejo se CALCULA de las marcas, no se lee de un campo
  // guardado. Tener las dos cosas —marcas y estado— es tener dos fuentes de
  // verdad para el mismo hecho: alcanzaría con que una quedara sin actualizar
  // para que un asiento discrepante siguiera sumando horas de inspección.
  const contados = incluirDiscrepantes
    ? propios
    : propios.filter((v) => evaluarCotejo(v.cotejo).estado !== ESTADOS_COTEJO.DISCREPA);

  const total = contados.reduce((a, v) => ({
    horasAeronave: a.horasAeronave + num(v.horasAeronave),
    horasMotor: a.horasMotor + num(v.horasMotor),
    ciclosNG: a.ciclosNG + num(v.ciclosNG),
    ciclosNF: a.ciclosNF + num(v.ciclosNF),
    ldg: a.ldg + num(v.ldg),
    cargas: a.cargas + num(v.cargas),
  }), { horasAeronave: 0, horasMotor: 0, ciclosNG: 0, ciclosNF: 0, ldg: 0, cargas: 0 });

  return {
    ...total,
    horasAeronave: redondear(total.horasAeronave),
    horasMotor: redondear(total.horasMotor),
    vuelos: contados.length,
    excluidosPorDiscrepancia: propios.length - contados.length,
    sinCotejar: propios.filter((v) => evaluarCotejo(v.cotejo).estado === ESTADOS_COTEJO.PENDIENTE).length,
  };
}

/** ¿Cae la fecha del asiento dentro del período?

    Exportada a propósito: las estadísticas agrupan los mismos asientos por
    aeronave, piloto, empresa y cliente, y si cada informe tuviera su propia idea
    de dónde termina el período —el 23:59:59 del último día, incluido— los
    totales de los cuatro cortes no darían lo mismo. */
export function enPeriodo(fechaISO, desde, hasta) {
  if (!fechaISO) return false;

  /* ═══ POR DÍA DE CALENDARIO, NO POR INSTANTE ═══

     Comparaba objetos `Date`, y ahí la zona horaria se mete sin avisar: el
     asiento se lee como `2026-03-10` → medianoche UTC, y el fin del período se
     armaba como `new Date("2026-03-09T23:59:59")`, que SIN la Z es hora LOCAL.
     En Ecuador (UTC-5) el 9 a las 23:59:59 local es el 10 a las 04:59 UTC, así
     que un vuelo del 10 caía dentro de un período que terminaba el 9: el
     informe se llevaba un día de más, y el total de un mes se comía el primero
     del siguiente.

     Un día de vuelo es una fecha del calendario, no un instante: se comparan
     las cadenas `AAAA-MM-DD`, que no tienen zona horaria y hacen el rango
     inclusivo en los dos extremos. */
  const dia = String(fechaISO).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return false;
  if (desde && dia < String(desde).slice(0, 10)) return false;
  if (hasta && dia > String(hasta).slice(0, 10)) return false;
  return true;
}

/**
 * Horas y DÍAS volados por un piloto en un período.
 *
 * Los días se cuentan por fecha distinta, no por cantidad de vuelos: tres
 * vuelos el mismo día son un día volado. Confundirlos infla el conteo que va
 * al control de descanso periódico —los 10 días al mes de Chile, las 24 h cada
 * 7 días de Ecuador y EE.UU.— y esos son días de calendario, no salidas.
 */
export function resumenPiloto(bitacora, pilotId, { desde = null, hasta = null } = {}) {
  const lista = Array.isArray(bitacora) ? bitacora : [];
  const propios = lista.filter(
    (v) => v && v.pilotId === pilotId && enPeriodo(v.fecha, desde, hasta),
  );
  const sinFecha = lista.filter((v) => v && v.pilotId === pilotId && !v.fecha).length;

  const dias = new Set(propios.map((v) => String(v.fecha).slice(0, 10)));
  const horas = propios.reduce((s, v) => s + num(v.horasAeronave), 0);

  return {
    horas: redondear(horas),
    diasVolados: dias.size,
    vuelos: propios.length,
    aterrizajes: propios.reduce((s, v) => s + num(v.ldg), 0),
    // Los asientos sin fecha no se pueden ubicar en ningún mes. No se
    // descartan en silencio: se cuentan aparte para que alguien los arregle.
    sinFecha,
    fechas: [...dias].sort(),
  };
}

/** Resumen del mes y del año en curso para un piloto. */
export function resumenMesYAnio(bitacora, pilotId, hoy = new Date()) {
  const anio = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, "0");
  const finMes = new Date(anio, hoy.getMonth() + 1, 0).getDate();
  return {
    mes: resumenPiloto(bitacora, pilotId, { desde: `${anio}-${mes}-01`, hasta: `${anio}-${mes}-${finMes}` }),
    anio: resumenPiloto(bitacora, pilotId, { desde: `${anio}-01-01`, hasta: `${anio}-12-31` }),
    etiquetaMes: `${anio}-${mes}`,
    etiquetaAnio: String(anio),
  };
}

/**
 * VERIFICACIÓN DE ARRASTRE — de punta a punta.
 *
 * Comprueba que el estado actual de la aeronave sea exactamente el de partida
 * más todo lo que la bitácora aportó. Es la comprobación que importa: si el
 * arrastre se rompe, el status de mantenimiento queda desfasado y los
 * remanentes de inspección mienten, sin que nada lo delate.
 *
 * Se compara con tolerancia porque las horas son decimales y sumar muchos
 * flotantes acumula error de representación. La tolerancia es de una décima:
 * por debajo de eso no es un error de arrastre, es aritmética binaria.
 *
 * @param {object} inicial estado de la aeronave antes de la bitácora
 * @param {object} actual estado de la aeronave hoy
 * @param {object} acumulado salida de acumuladoDeAeronave
 */
export function verificarArrastre(inicial, actual, acumulado, tolerancia = 0.1) {
  const campos = [
    { clave: "tsn", etiqueta: "Horas de célula (TSN)", aporte: acumulado.horasAeronave },
    { clave: "csn", etiqueta: "Ciclos de célula (CSN)", aporte: acumulado.ldg },
    { clave: "ldg", etiqueta: "Aterrizajes", aporte: acumulado.ldg },
    { clave: "cargas", etiqueta: "Cargas externas", aporte: acumulado.cargas },
  ];
  const camposMotor = [
    { clave: "tsn", etiqueta: "Horas de motor", aporte: acumulado.horasMotor },
    { clave: "cyNG", etiqueta: "Ciclos NG", aporte: acumulado.ciclosNG },
    { clave: "cyNF", etiqueta: "Ciclos NF", aporte: acumulado.ciclosNF },
  ];

  const revisar = (base, hoy, lista, prefijo) => lista.map((c) => {
    const esperado = num(base?.[c.clave]) + num(c.aporte);
    const real = num(hoy?.[c.clave]);
    const desvio = redondear(real - esperado, 3);
    return {
      campo: `${prefijo}${c.etiqueta}`,
      inicial: num(base?.[c.clave]),
      aporte: num(c.aporte),
      esperado: redondear(esperado, 3),
      real,
      desvio,
      coincide: Math.abs(desvio) <= tolerancia,
    };
  });

  const filas = [
    ...revisar(inicial, actual, campos, ""),
    ...revisar(inicial?.motor, actual?.motor, camposMotor, "Motor — "),
  ];

  const desvios = filas.filter((f) => !f.coincide);
  return {
    filas,
    coincide: desvios.length === 0,
    desvios,
    mensaje: desvios.length === 0
      ? "El status de la aeronave coincide con la suma de la bitácora."
      : `${desvios.length} campo(s) no cuadran con la bitácora: ${desvios.map((d) => `${d.campo} (${d.desvio > 0 ? "+" : ""}${d.desvio})`).join(", ")}.`,
  };
}



/* ============================================================================
   VENTANAS MÓVILES

   El resumen por mes y año no alcanza. Los límites de horas de vuelo son de
   **días consecutivos**, no de mes calendario:

     8 h en 24 h (monopiloto)
     40 h en 7 días
     100 h en 28 días
     1000 h en 365 días

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ NO SIRVE LA SEMANA CALENDARIO

   Un piloto puede volar 39 h del lunes al domingo y 39 h del lunes siguiente
   al domingo. En las dos semanas calendario está dentro del límite. Pero del
   miércoles al martes voló 60 h.

   **El límite es de siete días consecutivos.** Calcular por semana calendario
   da una respuesta que parece correcta y no lo es — y es peor que no calcular
   nada, porque nadie la revisa.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/**
 * Límites de horas de vuelo por ventana.
 *
 * Los de BARS, que es el estándar que el cliente audita. La autoridad local
 * puede ser más exigente: si el operador declara otros, mandan los suyos.
 */
export const LIMITES_VENTANA = [
  { dias: 1, horas: 8, nombre: "24 horas", nota: "Monopiloto." },
  { dias: 7, horas: 40, nombre: "7 días" },
  { dias: 28, horas: 100, nombre: "28 días" },
  { dias: 365, horas: 1000, nombre: "365 días" },
];

/** Fracción del límite a partir de la cual conviene avisar. */
export const AVISO_A_FRACCION = 0.9;

/** Días entre dos fechas ISO, sin depender de la zona horaria. */
function diasEntre(desdeISO, hastaISO) {
  const a = Date.parse(`${String(desdeISO).slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${String(hastaISO).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
}

/**
 * Horas voladas en los últimos N días, contando hacia atrás desde una fecha.
 *
 * La ventana INCLUYE el día de referencia: «últimos 7 días» son hoy y los seis
 * anteriores, que es como lo cuenta la norma. Contar siete hacia atrás sin
 * incluir hoy daría ocho días de ventana.
 */
export function horasEnVentana(bitacora, pilotId, dias, hastaFecha) {
  const lista = Array.isArray(bitacora) ? bitacora : [];
  const n = num(dias);
  if (n === null || n < 1) return null;

  const hasta = String(hastaFecha || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(hasta)) return null;

  let horas = 0;
  let vuelos = 0;

  for (const v of lista) {
    if (!v || v.pilotId !== pilotId || !v.fecha) continue;

    /* Los ARRASTRES no entran: llevan la fecha hasta la que cubren, no la de
       un vuelo. Hoy quedan fuera igual porque no tienen `horasAeronave`, pero
       eso es un accidente de la forma del dato — si alguien se lo agregara,
       una carrera entera aparecería como volada en los últimos siete días.

       La regla se declara para que no dependa del accidente. */
    if (v.entrada === "arrastre") continue;

    const d = diasEntre(v.fecha, hasta);
    /* `d` es cuántos días atrás quedó el vuelo. Cero es hoy; n−1 es el más
       viejo que entra. Un vuelo con fecha FUTURA (d negativo) no entra: sería
       contar algo que no pasó. */
    if (d === null || d < 0 || d > n - 1) continue;
    horas += num(v.horasAeronave) ?? 0;
    vuelos += 1;
  }

  return { dias: n, hasta, horas: redondear(horas), vuelos };
}

/**
 * Estado del piloto contra todas las ventanas.
 *
 * Avisa ANTES de pasarse: un piloto que ya voló 39 h en 6 días necesita
 * saberlo antes de aceptar el vuelo de mañana, no después de volarlo.
 */
export function ventanasDePiloto(bitacora, pilotId, hastaFecha, { limites = LIMITES_VENTANA } = {}) {
  const hasta = String(hastaFecha || "").slice(0, 10);

  const ventanas = (Array.isArray(limites) ? limites : []).map((l) => {
    const v = horasEnVentana(bitacora, pilotId, l.dias, hasta);
    if (!v) return { ...l, horas: null, disponible: null, estado: "sin-dato" };

    const disponible = redondear(l.horas - v.horas);
    const fraccion = l.horas > 0 ? v.horas / l.horas : 0;

    return {
      ...l,
      horas: v.horas,
      vuelos: v.vuelos,
      disponible,
      fraccion,
      estado: fraccion > 1 ? "excedido" : (fraccion >= AVISO_A_FRACCION ? "cerca" : "bien"),
    };
  });

  const excedidas = ventanas.filter((v) => v.estado === "excedido");
  const cerca = ventanas.filter((v) => v.estado === "cerca");

  /* Lo que el piloto puede volar HOY es lo que le deje la ventana más
     restrictiva, no la de 24 h: si le quedan 2 h en los 7 días, no puede volar
     8 aunque el límite diario se lo permita. */
  const disponibles = ventanas.map((v) => v.disponible).filter((d) => d !== null);
  const margenReal = disponibles.length > 0 ? Math.max(0, Math.min(...disponibles)) : null;
  const restrictiva = ventanas.find((v) => v.disponible === Math.min(...disponibles)) || null;

  return {
    hasta,
    ventanas,
    excedidas,
    cerca,
    margenReal,
    restrictiva: restrictiva ? restrictiva.nombre : null,
    resumen: excedidas.length > 0
      ? `Excedido en ${excedidas.map((v) => v.nombre).join(", ")}.`
      : (cerca.length > 0
        ? `Cerca del límite en ${cerca.map((v) => v.nombre).join(", ")}. `
          + `Quedan ${margenReal} h por ${restrictiva?.nombre}.`
        : (margenReal !== null ? `Quedan ${margenReal} h por ${restrictiva?.nombre}.` : null)),
  };
}

/**
 * ¿Puede volar estas horas hoy?
 *
 * Responde la pregunta que se hace el jefe de operaciones al asignar un vuelo,
 * y dice cuál ventana lo impide — «no puede» sin el motivo obliga a revisar
 * las cuatro a mano.
 */
export function puedeVolar(bitacora, pilotId, horasPrevistas, hastaFecha, opciones = {}) {
  /* `num` de este módulo devuelve 0 cuando no puede convertir —sirve para
     sumar horas sin ensuciar los totales— pero acá esa conversión autorizaba
     el vuelo: `null` pasaba como «cero horas previstas», que siempre cabe.

     Se valida ANTES de convertir. */
  const crudo = typeof horasPrevistas === "string" ? horasPrevistas.trim() : horasPrevistas;
  const h = (crudo === null || crudo === undefined || crudo === ""
    || !Number.isFinite(Number(crudo)))
    ? null
    : Number(crudo);

  if (h === null || h < 0) {
    return { puede: false, motivo: "Las horas previstas no son un número válido." };
  }

  const estado = ventanasDePiloto(bitacora, pilotId, hastaFecha, opciones);
  if (estado.margenReal === null) {
    return { puede: false, motivo: "No se pudo calcular: revisa la fecha." };
  }

  if (estado.excedidas.length > 0) {
    return {
      puede: false,
      motivo: `Ya está excedido en ${estado.excedidas.map((v) => v.nombre).join(", ")}.`,
      margenReal: estado.margenReal,
    };
  }

  if (h > estado.margenReal) {
    return {
      puede: false,
      motivo: `${h} h supera lo disponible: quedan ${estado.margenReal} h por `
        + `el límite de ${estado.restrictiva}.`,
      margenReal: estado.margenReal,
    };
  }

  return { puede: true, motivo: null, margenReal: estado.margenReal, estado };
}

/**
 * Reporte por piloto y período, para exportar.
 *
 * Es lo que pide la autoridad en una inspección y lo que pide el cliente BARS:
 * horas por piloto, por período, con los asientos sin fecha listados aparte
 * para que alguien los arregle.
 */
export function reportePorPiloto(bitacora, pilotos = [], { desde = null, hasta = null, hoy = null } = {}) {
  const lista = Array.isArray(pilotos) ? pilotos : [];
  const fechaCorte = hasta || hoy || new Date().toISOString().slice(0, 10);

  return {
    desde,
    hasta: fechaCorte,
    generado: new Date().toISOString(),
    pilotos: lista.map((p) => {
      const id = p?.id ?? p;
      const nombre = p?.nombre ?? String(id);
      const resumen = resumenPiloto(bitacora, id, { desde, hasta: fechaCorte });
      const ventanas = ventanasDePiloto(bitacora, id, fechaCorte);

      return {
        id,
        nombre,
        ...resumen,
        ventanas: ventanas.ventanas,
        excedidas: ventanas.excedidas.map((v) => v.nombre),
        margenReal: ventanas.margenReal,
      };
    }),
  };
}
